import React from "react";
import { useRef, useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Marquee from "react-fast-marquee";

gsap.registerPlugin(ScrollTrigger);

const experticeData = [
  {
    id: 1,
    number: "01",
    title: "Enter Listing Info",
    subtitle: [" Add property details like location, price, and features."],
    icon: "assets/images/icons/info.png",
  },
  {
    id: 2,
    number: "02",
    title: "Upload Property Images",
    subtitle: ["Drag and drop 5–7 images. Easy and quick setup."],
    icon: "assets/images/icons/upload.png",
  },
  {
    id: 3,
    number: "03",
    title: "Set Preferences & Create",
    subtitle: [
      "Choose background music, and video size— then launch with one click.",
    ],
    icon: "assets/images/icons/play.png",
  },
];

const featureCards = [
  {
    title: "Instagram",
    description:
      "Perfect for Instagram, YouTube Shorts, and TikTok. Optimized format for mobile.",
    icon: "assets/images/icons/logo_instagram.png",
  },
  {
    title: "Youtube Shorts",
    description:
      "Automatically adds your brand color, logo, and consistent design elements.",
    icon: "assets/images/icons/logo_youtube.png",
  },
  {
    title: "Tiktok",
    description:
      "Automatically inserts frames for promotions and narrates using AI voice.",
    icon: "assets/images/icons/logo_tiktok.png",
  },
];

const benefits = [
  {
    stat: "60×",
    tags: "x",
    label: "Faster Production",
  },
  {
    stat: "100",
    tags: "x",
    label: "Cost Efficiency",
  },
  {
    stat: "100",
    tags: "%",
    label: "Workflow Automation",
  },
];

const ctaLeftImages = [
  "assets/images/house/h1.png",
  "assets/images/house/h2.png",
  "assets/images/house/h3.png",
  "assets/images/house/h4.png",
];

const ctaRightImages = [
  "assets/images/house/h5.png",
  "assets/images/house/h6.png",
  "assets/images/house/h7.png",
  "assets/images/house/h8.png",
];

export default function LandingPage() {
  const cardContainer = useRef();

  useEffect(() => {
    const cards = cardContainer.current.querySelectorAll(".card");
    gsap.fromTo(
      cards,
      { autoAlpha: 0, x: 100 }, // 왼쪽에서 시작
      {
        autoAlpha: 1,
        x: 0,
        duration: 1,
        ease: "power3.out",
        stagger: 0.2,
        scrollTrigger: {
          trigger: cardContainer.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      }
    );
  }, []);

  const counterRef = useRef();

  useEffect(() => {
    const counters = counterRef.current.querySelectorAll(".stat");

    counters.forEach((el) => {
      const target = +el.getAttribute("data-target");

      gsap.fromTo(
        el,
        { innerText: 0 },
        {
          innerText: target,
          duration: 2,
          ease: "power1.out",
          snap: { innerText: 1 },
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none none",
          },
          onUpdate: () => {
            el.innerText = Math.floor(el.innerText);
          },
        }
      );
    });
  }, []);

  return (
    <div className="bg-black text-gray-800 font-sans ">
      <header className="fixed top-0 left-0 w-full z-50 px-6 py-4 flex items-center justify-between text-white">
        <div className="flex items-center gap-3">
          <img src="/Logo.svg" alt="Palette Logo" className="h-8" />
        </div>
        <nav className="flex gap-6 text-sm md:text-base">
          <a href="#features" className="hover:underline transition">
            Features
          </a>
          <a href="/login" className="hover:underline transition">
            Login
          </a>
        </nav>
      </header>
      {/* Why Video Section */}
      <section
        className="relative h-screen bg-cover bg-center flex items-center justify-center text-white"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=3550&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')",
        }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute top-0 left-0 w-full h-full object-cover z-0"
        >
          <source
            src="https://capsule-public-objs.s3.amazonaws.com/projects/5a5addfb-245f-47cd-a74c-885747a6bef9/final_video.mp4"
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center px-4">
          <span className="animate-pulse text-5xl md:text-[3.5vw] font-light tracking-tight text-white">
            Forget Editing - Just Upload Images
          </span>
          <h1
            data-aos="fade-up"
            data-aos-easing="linear"
            data-aos-duration="1000"
            className="text-5xl md:text-[6vw] leading-tight mb-6"
          >
            AI-generated
            <br />
            Real Estate Video
          </h1>
          <a
            data-aos="fade-up"
            data-aos-easing="linear"
            data-aos-duration="2000"
            href="#create"
            className="text-xl font-thin inline-flex items-center gap-2 bg-white/30 border border-white/80 text-white px-6 py-3 rounded-full shadow hover:bg-black transition"
          >
            Try it Free
            <img
              src="https://pltt.xyz/resource/images/icon/wh-arrow.svg"
              alt="arrow"
              className="w-4 h-4"
            />
          </a>
        </div>
      </section>
      {/* Comparison Section */}
      <section className="relative bg-black py-40 px-4 text-white overflow-hidden">
        {/* 배경 이미지 (20% 불투명도) */}
        <div className="absolute inset-0 bg-center bg-cover opacity-10 z-0"></div>
        <div className="absolute inset-0 bg-center bg-cover opacity-50 z-0"></div>
        <div className="relative z-10">
          <h2 className="text-5xl md:text-[4vw] lg:text-[7xl] text-center mb-12">
            Automate Your Video Creation
          </h2>
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
            {/* Traditional Method */}
            <div className="p-6 rounded-xl shadow-lg border border-gray-700 py-20">
              <p className="text-sm font-semibold mb-2 bg-gray-800 inline-block px-3 py-1 rounded-full text-white">
                Powered by AI Technology
              </p>
              <h3 className="text-5xl md:text-[3vw] mb-4">
                Finished in
                <br />
                5–15 minutes
              </h3>
              <ul className="space-y-3 text-gray-300 list-disc list-inside">
                <li>No need to shoot, edit, or outsource.</li>
                <li>Just drag in your images,</li>
                <li>No filming required</li>
                <li>No manual editing</li>
                <li>No outsourced editor</li>
                <li>and our system auto-generates a finished video.</li>
              </ul>
            </div>

            {/* AI-Powered Method */}
            <div className="relative bg-cover bg-center items-center justify-center">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="absolute top-0 left-0 object-cover z-0"
              >
                <source src="assets/video/howto.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-36 px-4 text-white">
        <div className="max-w-5xl mx-auto">
          <span>How It Works</span>
          <h2 className="text-5xl md:text-[4vw] lg:text-[7xl] mb-12">
            Just 3 Steps
          </h2>
          <div className="space-y-10">
            {experticeData.map((map) => (
              <div
                className="flex items-center justify-between border-t pt-6 gap-6"
                data-aos="fade-up"
                data-aos-easing="linear"
                data-aos-duration={map.id * 400}
              >
                <div className="flex items-center gap-4">
                  <img
                    src={map.icon}
                    className="w-36 h-36 rounded-md flex-shrink-0"
                  />
                  <div>
                    <div className="text-2xl font-bold">{map.title}</div>
                    <p className="text-gray-400 mt-1">{map.subtitle}</p>
                  </div>
                </div>
                <h2 className="text-5xl md:text-[5vw] lg:text-[7xl">
                  {map.number}
                </h2>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-black text-white pt-40 px-4" ref={cardContainer}>
        <div
          className="absolute inset-0 bg-center bg-cover opacity-10 z-0"
          style={{
            backgroundImage:
              "url('https://previews.dropbox.com/p/thumb/ACr8bnvE5of8pGu6KUgouURKnmYRkRI27yYQ5BDsBJ5L4Dow3IUHsgO3rqskcNzAzNcX8xBE6Y1iRFX9S-j6nuNqsGFVGuTN0yQLeiQ_LB8BrNG3yBJDFUlRFj4HJiutnZ1WcNnBxgpoVhqL8f2WbtLPLiUEeXCF2bptdDdw8a3drrAXaFYg75JmuGrvY51mKqhSyB0kpbv4BmRGBM4OsjZ1DS8SX4HLKsT57A8vLTPFuokfIuM31pd7cfbIuZlI6GuELWQhg3iLjjjgTD0zBK3lAUkYLH8ztJpzv-8A7Sobfg9-yyzHjF8ntcDUb-CIS9_6GyGjxG-8DuENj1VJS2lt/p.png?is_prewarmed=true')",
          }}
        ></div>
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <h2 className="text-5xl md:text-[4vw] lg:text-[7xl] text-center mb-12">
              Use It Right Away for Marketing
              <br />
              Social-Ready Short Videos
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-8">
            {featureCards.map((item, index) => (
              <div key={index} className="card p-6 text-center shadow-md">
                <div className="rounded-xl mx-auto flex my-auto items-center justify-center">
                  <img
                    src={item.icon}
                    className="h-16 rounded-md flex-shrink-0"
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="relative rounded-xl h-[50vh] bg-cover bg-center items-center justify-center">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute rounded-t-3xl top-0 left-0 w-full h-full object-cover z-0"
            >
              <source src="assets/video/usesns.MP4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Why Now */}
      <section className="py-60 px-4 text-center text-black" ref={counterRef}>
        <div className="max-w-4xl mx-auto mb-12 text-white">
          <h2 className="text-5xl md:text-[4vw] lg:text-[7xl] mb-12">
            Why Adopt It Now?
          </h2>
          <p className="text-gray-600 text-base md:text-lg">
            Scale your content creation effortlessly with automation — faster,
            smarter, and without the overhead.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {benefits.map((item, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl shadow-sm py-10 px-6"
            >
              <div className="text-5xl md:text-[7vw] lg:text-[7xl] mb-2 font-thin flex items-baseline justify-center stat-wrapper">
                <span
                  className="stat"
                  data-target={item.stat.replace(/[^0-9]/g, "")}
                >
                  0
                </span>
                <span className="ml-1">{item.tags}</span>
              </div>

              <div className="text-xl text-gray-500 uppercase">
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className=" text-white relative flex h-[35.8vw] bg-black  items-center justify-center">
        {/* 중앙 텍스트 (앞에 보여야 함) */}
        <div className="absolute  z-10 text-center">
          <p className="text-lg mb-4">
            Upload your images now and create your own listing video.
          </p>
          <h2 className="text-5xl md:text-[4vw] lg:text-[7xl] mb-12">
            Try It Free Now
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
            {/* <a
              href="#create"
              className="text-xl font-thin inline-flex items-center gap-2 bg-white/30 border border-white/80 text-white px-6 py-3 rounded-full shadow hover:bg-black transition"
            >
              View Plans
              <img
                src="https://pltt.xyz/resource/images/icon/wh-arrow.svg"
                alt="arrow"
                className="w-4 h-4"
              />
            </a> */}
            <a
              href="#create"
              className="text-xl font-thin inline-flex items-center gap-2 bg-white border border-white/80 text-black px-6 py-3 rounded-full shadow hover:bg-white/70 transition"
            >
              Create a Free Video
              <img
                src="https://pltt.xyz/resource/images/icon/bl-arrow.svg"
                alt="arrow"
                className="w-4 h-4"
              />
            </a>
          </div>
        </div>

        {/* 좌우 마퀴 이미지들 (뒤에 보이게 z-0) */}
        <div className="absolute inset-0 flex z-0">
          <Marquee direction="down" speed={30} className="h-full flex flex-col">
            {ctaLeftImages.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`cta-${index}`}
                className={`w-60 rounded-xl m-24 ${
                  index % 2 === 0 ? "rotate-[6deg]" : "rotate-[-6deg]"
                }`}
              />
            ))}
          </Marquee>
          <div className="h-full w-[80vh] flex flex-col"></div>
          <Marquee direction="up" speed={30} className="h-full flex flex-col">
            {ctaRightImages.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`cta-${index}`}
                className={`w-60 rounded-xl m-24 ${
                  index % 2 === 0 ? "rotate-[6deg]" : "rotate-[-6deg]"
                }`}
              />
            ))}
          </Marquee>
        </div>
      </section>
      {/* Why Now */}
      <section className="bg-[#f5f8fb] py-32 px-6 text-black">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16">
          <div>
            <p className="text-sm text-gray-400">Main — Contact</p>
            <h2 className="text-[6vw] md:text-[5vw] font-light tracking-tight  border-gray-300 pb-2">
              CONTACTS
            </h2>
          </div>
          <div className="space-y-6 text-l mt-10">
            <div className="flex justify-between border-b border-gray-300 pb-2">
              <span className="text-gray-500">Email</span>
              <span>yejin@pltt.xyz</span>
            </div>

            <div className="flex justify-between border-b border-gray-300 pb-2">
              <span className="text-gray-500">Address</span>
              <span className="text-right">
                1601 Elm St, Dallas,
                <br />
                TX 75201
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
